import { CIActionBtnGroup, CIActionGuard, CITableGrid, CITooltip, CircleBadge, Loader, getLocaleDateTimeFormat, toLocalDate, useToastMessage, useUserPermission } from 'ccs-ui-components';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { requiredAuthPermission } from '../../../../../common/constants/UserPermissionConstants';
import { IServiceScheduleMaintenance, ImpactedService } from '../../../../models';
import { AppDispatch } from '../../../../store/AppStore';
import { cleargetSmLogHistory, getSmLogHistoryRequest } from '../../../../store/actions/sagaActions/schedule-maintenance/SmLogHistorySagaAction';
import { clearSaveMaintenance } from '../../../../store/reducer/schedule-maintenance/SaveMaintenanceReducer';
import { clearSmLogHistory } from '../../../../store/reducer/schedule-maintenance/SmLogHistoryReducer';
import { SCHEDULE_MAINTENANCE_ROUTE } from '../../schedule-maintenance-wrapper/scheduled-maintenance-details-wrapper/utilities/AddScheduleMaintenanceUtils';
import classes from '../MaintenanceList.module.scss';
import MaintenanceLog from '../maintenance-log-history/MaintenanceLogHistory';

interface IMaintenanceTableList {
  handleClick: (e: React.MouseEvent<HTMLElement>, label: string) => void;
  renderMaintenanceStatus: (item: IServiceScheduleMaintenance) => React.JSX.Element;
  sortedObj: {
    sortedName: string;
    sortedOrder: string;
  };
  footerObj: {
    itemsPerPage: number;
    currentPageNumber: number;
    numberOfPages: number; // Update this based on your actual type
    onCurrentPageNumberChange: (pageNumber: number) => void;
    onItemsPerPageChange: (itemsPerPage: number) => void;
  };
  handleDelete: (name: string) => void;
}

const MaintenanceTableList = (props: IMaintenanceTableList) => {
  const { t: translate } = useTranslation();
  const route = useLocation();
  const { handleClick, renderMaintenanceStatus, sortedObj, footerObj, handleDelete } = props;
  const navigate = useNavigate();
  const { permissionObject } = useUserPermission();
  const [showLog, setShowLog] = useState(false);
  const [loading, setLoading] = useState(false);
  const { ScheduledMaintenanceDetails } = useSelector(
    (state: any) => state?.ScheduledMaintenanceReducer
  );
  const { failure, renderToast } = useToastMessage();

  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, errorMessage } = useSelector(
    (state: any) => state?.LogHistory
  );

  const openModal = (row: any) => {
    const payload = {
      maintenanceName: row.maintenanceName,
      status: row.status
    };
    setLoading(true);
    dispatch(getSmLogHistoryRequest(payload));
  };

  useEffect(() => {
    if (!isLoading && loading) {
      setLoading(false);
      setShowLog(true);
    }
    if (errorMessage && !isLoading && !loading) {
      setLoading(false);
      setShowLog(false);
      failure(translate('sm_table_wrapper.txt_error'), translate('maintenance_table.txt_error_desc'));
      dispatch(cleargetSmLogHistory());
    }
  }, [isLoading, errorMessage, loading]);

  const renderMaintenanceName = (item: IServiceScheduleMaintenance) => (
    <div className={classes['first-col']}>
      <span
        className={classes['cp-capsule-wrapped-title']}
        title={item.maintenanceName}
      >
        {item.maintenanceName}
      </span>
    </div>
  );

  const messageForTooltip = (item: ImpactedService[]) => (
    <ol className={classes['tooltip-badge-wrapper']}> {item
      .map((item: ImpactedService) => (
        <li
          key={item.serviceName}
          className={classes['tooltip-badge']}
        >
          {item.serviceName}
        </li>))}
    </ol>
  );

  const renderImpactedComponents = (item: any) => (
    <div className={classes['role-cell']}>
      {item.impactedServicesList.map((item: ImpactedService, index: number) => {
        if (index < 1) {
          return (
            <span
              key={item.serviceName}
              className={classes['cp-capsule-wrapped-title']}
              title={item.serviceName}
            >
              {item.serviceName}
            </span>
          );
        }
        return null;
      })}
      <CITooltip placement="right" message={messageForTooltip(item.impactedServicesList)}>
        <div className={classes['role-cell-div']}>
          {item.impactedServicesList.length - 1 > 0 ? (
            <CircleBadge
              count={`+${item.impactedServicesList.length - 1}`}
            />
          ) : null}
        </div>
      </CITooltip>
    </div>
  );

  const renderStartTime = (item: any) => (
    <div className={classes['date-holder']}>
      <div className={classes['cp-capsule-wrapped-title']} title={toLocalDate({ date: item.startTimestamp })}>{moment(item.startTimestamp).format('dddd').substring(0, 3)} {toLocalDate({ date: item.startTimestamp })}</div>
      <div className={classes['cp-capsule-wrapped-title']} title={toLocalDate({ date: item.endTimestamp })}>{translate('maintenance_table.txt_to')} {moment(item.endTimestamp).format('dddd').substring(0, 3)} {toLocalDate({ date: item.endTimestamp })}</div>
    </div>
  );

  const renderUpdatedTimestamp = (item: any) => (
    <div className={classes['last-modified-holder']}>
      <div className={classes['cp-capsule-wrapped-title']} title={String(toLocalDate({ date: item.updatedTimestamp }))}>{String(toLocalDate({ date: item.updatedTimestamp }))}</div>
      <div className={classes['cp-capsule-wrapped-title']} title={item.updatedBy}> {translate('maintenance_table.txt_by')}{item.updatedBy}</div>
    </div>
  );

  const maintenanceAction = (maintenance: any, actionType: string) => {
    dispatch(clearSaveMaintenance());
    dispatch(clearSmLogHistory());
    navigate(
      `${SCHEDULE_MAINTENANCE_ROUTE.SCHEDULE_MAINTENANCE_HOME}/${actionType.toLowerCase()}/${maintenance.maintenanceName}`,
      {
        state: {
          path: route.search,
          ...maintenance,
          actionType
        }
      }
    );
  };

  const renderActionButton = (row: any) => (
    <CIActionBtnGroup
      buttons={[
        {
          label: 'audit',
          key: 'timerIcon',
          onClick: () => openModal(row),
          disabled: row.maintenanceName.trim().length === 0
        },
        {
          label: 'view',
          key: 'viewIcon',
          onClick: () => maintenanceAction(row, 'View')
        },
        {
          label: 'edit',
          key: 'editIcon',
          onClick: () => maintenanceAction(row, 'Edit'),
          disabled: ((row.maintenanceStatus === 'COMPLETED' || row.maintenanceStatus === 'IN PROGRESS') && CIActionGuard([requiredAuthPermission.Maintainence.edit], permissionObject.permission))
        },
        {
          label: 'delete',
          key: 'deleteIcon',
          onClick: () => handleDelete(row.maintenanceName),
          disabled: ((row.maintenanceStatus === 'COMPLETED' || row.maintenanceStatus === 'IN PROGRESS') && CIActionGuard([requiredAuthPermission.Maintainence.edit], permissionObject.permission))
        }
      ]}
    />
  );

  return (
    <>
      {renderToast()}
      <CITableGrid
        headers={[
          {
            label: translate('maintenance_table.txt_maintenance_name'),
            name: 'maintenanceName',
            options: { breakPointWidth: 2 },
            sortOptions: {
              callback: handleClick
            },
            columnData: {
              field: 'maintenanceName',
              render: renderMaintenanceName
            }
          },
          {
            label: translate('maintenance_table.txt_impacted_components'),
            name: 'impactedServices',
            options: { breakPointWidth: 2 },
            columnData: {
              field: 'impactedServices',
              render: renderImpactedComponents
            }
          },
          {
            label: translate('maintenance_table.txt_maintenance_status'),
            name: 'maintenanceStatus',
            options: { breakPointWidth: 2 },
            columnData: {
              field: 'maintenanceStatus',
              render: renderMaintenanceStatus
            }
          },
          {
            label: translate('maintenance_table.txt_start_end_date'),
            labelOptions: { helperText: `(${getLocaleDateTimeFormat()})`, helperTextClass: 'dateon-sublabel' },
            name: 'startTimestamp',
            options: { breakPointWidth: 2 },
            sortOptions: {
              callback: handleClick
            },
            columnData: {
              field: 'startTimestamp',
              render: renderStartTime
            }
          },
          {
            label: translate('maintenance_table.txt_last_modified'),
            labelOptions: { helperText: `(${getLocaleDateTimeFormat()})`, helperTextClass: 'dateon-sublabel' },
            name: 'updatedTimestamp',
            options: { breakPointWidth: 2 },
            sortOptions: {
              callback: handleClick
            },
            columnData: { field: 'updatedTimestamp', render: renderUpdatedTimestamp }
          },
          {
            label: '',
            name: 'action',
            options: { breakPointWidth: 2 },
            columnData: { field: 'action', render: renderActionButton, className: 'button-alignment' }
          }
        ]}
        sorted={sortedObj}
        footer={{
          footerType: 'paging',
          footerOptions: footerObj
        }}
        tableData={ScheduledMaintenanceDetails?.serviceScheduleMaintenance}
      />
      {loading && <Loader show />}
      <Modal
        size="lg"
        show={showLog}
        onHide={() => {
          setShowLog(false);
        }}
        backdrop="static"
        keyboard={false}
        aria-label={String(translate('maintenance_table.txt_log_history'))}
        centered
        alignToCentre
      >
        <Modal.Header closeButton className={classes.modalTitle}>{translate('maintenance_table.txt_log_history')}</Modal.Header>
        <MaintenanceLog />
      </Modal>
    </>
  );
};
export default MaintenanceTableList;
